import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreateSubscriptionInput,
  CustomerDto,
  SubscriptionDto,
} from '@parking/shared';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

type CustomerForm = {
  code: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  taxId: string;
  companyName: string;
};

type SubForm = {
  plan: CreateSubscriptionInput['plan'];
  status: NonNullable<CreateSubscriptionInput['status']>;
  priceCents: string;
  startsAt: string;
  endsAt: string;
  autoRenew: boolean;
};

const emptyCustomer: CustomerForm = {
  code: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  taxId: '',
  companyName: '',
};

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function defaultSubForm(): SubForm {
  return {
    plan: 'MONTHLY',
    status: 'ACTIVE',
    priceCents: '9900',
    startsAt: toLocalInput(new Date().toISOString()),
    endsAt: '',
    autoRenew: true,
  };
}

export function CustomersPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('customers:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('customers:write'));
  const canReadSub = useAuthStore((s) => s.hasPermission('subscriptions:read'));
  const canWriteSub = useAuthStore((s) => s.hasPermission('subscriptions:write'));
  const canWriteVehicle = useAuthStore((s) => s.hasPermission('vehicles:write'));

  const [selectedId, setSelectedId] = useState('');
  const [customerDialog, setCustomerDialog] = useState(false);
  const [subDialog, setSubDialog] = useState(false);
  const [linkDialog, setLinkDialog] = useState(false);
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [editingSubId, setEditingSubId] = useState<string | null>(null);
  const [form, setForm] = useState<CustomerForm>(emptyCustomer);
  const [subForm, setSubForm] = useState<SubForm>(defaultSubForm);
  const [linkVehicleId, setLinkVehicleId] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => api.listCustomers(),
    enabled: canRead,
  });

  const activeId = selectedId || customers.data?.data[0]?.id || '';

  const vehicles = useQuery({
    queryKey: ['vehicles', activeId],
    queryFn: () => api.listVehicles(activeId),
    enabled: canRead && Boolean(activeId),
  });

  const allVehicles = useQuery({
    queryKey: ['vehicles'],
    queryFn: () => api.listVehicles(),
    enabled: linkDialog,
  });

  const subscriptions = useQuery({
    queryKey: ['subscriptions', activeId],
    queryFn: () => api.listSubscriptions(activeId),
    enabled: canReadSub && Boolean(activeId),
  });

  const closeCustomerDialog = () => {
    setCustomerDialog(false);
    setEditingCustomerId(null);
    setForm(emptyCustomer);
    setFormError(null);
  };

  const closeSubDialog = () => {
    setSubDialog(false);
    setEditingSubId(null);
    setSubForm(defaultSubForm());
    setFormError(null);
  };

  const openCreateCustomer = () => {
    setFormError(null);
    setEditingCustomerId(null);
    setForm(emptyCustomer);
    setCustomerDialog(true);
  };

  const openEditCustomer = (c: CustomerDto) => {
    setFormError(null);
    setEditingCustomerId(c.id);
    setForm({
      code: c.code,
      firstName: c.firstName,
      lastName: c.lastName,
      email: c.email ?? '',
      phone: c.phone ?? '',
      taxId: c.taxId ?? '',
      companyName: c.companyName ?? '',
    });
    setCustomerDialog(true);
  };

  const openCreateSub = () => {
    setFormError(null);
    setEditingSubId(null);
    setSubForm(defaultSubForm());
    setSubDialog(true);
  };

  const openEditSub = (s: SubscriptionDto) => {
    setFormError(null);
    setEditingSubId(s.id);
    setSubForm({
      plan: s.plan,
      status: s.status,
      priceCents: String(s.priceCents),
      startsAt: toLocalInput(s.startsAt),
      endsAt: s.endsAt ? toLocalInput(s.endsAt) : '',
      autoRenew: s.autoRenew,
    });
    setSubDialog(true);
  };

  const saveCustomer = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        taxId: form.taxId.trim() || null,
        companyName: form.companyName.trim() || null,
      };
      if (editingCustomerId) {
        return api.updateCustomer(editingCustomerId, payload);
      }
      return api.createCustomer(payload);
    },
    onSuccess: async (res) => {
      closeCustomerDialog();
      await qc.invalidateQueries({ queryKey: ['customers'] });
      setSelectedId(res.data.id);
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteCustomer = useMutation({
    mutationFn: (id: string) => api.deleteCustomer(id),
    onSuccess: async () => {
      setSelectedId('');
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const saveSub = useMutation({
    mutationFn: async () => {
      const payload = {
        plan: subForm.plan,
        status: subForm.status,
        priceCents: Number(subForm.priceCents) || 0,
        startsAt: new Date(subForm.startsAt).toISOString(),
        endsAt: subForm.endsAt ? new Date(subForm.endsAt).toISOString() : null,
        autoRenew: subForm.autoRenew,
      };
      if (editingSubId) {
        return api.updateSubscription(editingSubId, payload);
      }
      return api.createSubscription({ customerId: activeId, ...payload });
    },
    onSuccess: async () => {
      closeSubDialog();
      await qc.invalidateQueries({ queryKey: ['subscriptions', activeId] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const deleteSub = useMutation({
    mutationFn: (id: string) => api.deleteSubscription(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['subscriptions', activeId] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  const linkVehicle = useMutation({
    mutationFn: () => api.linkVehicle(activeId, { vehicleId: linkVehicleId, isPrimary: true }),
    onSuccess: async () => {
      setLinkDialog(false);
      setLinkVehicleId('');
      setFormError(null);
      await qc.invalidateQueries({ queryKey: ['vehicles', activeId] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (e: Error) => setFormError(e.message),
  });

  const unlinkVehicle = useMutation({
    mutationFn: (vehicleId: string) => api.unlinkVehicle(activeId, vehicleId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['vehicles', activeId] });
      await qc.invalidateQueries({ queryKey: ['customers'] });
    },
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('customers.forbidden')}</div>;
  }

  const customerRows = customers.data?.data ?? [];
  const vehicleRows = vehicles.data?.data ?? [];
  const subRows = subscriptions.data?.data ?? [];

  const customerColumns = [
    t('customers.code'),
    t('customers.name'),
    t('customers.email'),
    t('customers.phone'),
    t('customers.vehicles'),
    t('customers.subs'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  const vehicleColumns = [
    t('vehicles.plate'),
    t('vehicles.brand'),
    t('vehicles.model'),
    t('vehicles.type'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  const subColumns = [
    t('customers.plan'),
    t('customers.status'),
    t('customers.price'),
    t('customers.startsAt'),
    t('customers.endsAt'),
    ...(canWriteSub ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={openCreateCustomer}>
              {t('customers.create')}
            </button>
          ) : undefined
        }
      />

      {customers.isError && (
        <div className="alert alert-danger">{(customers.error as Error).message}</div>
      )}

      <Card className="mb-4">
        <ParkingDataTable
          storageKey="customers"
          columns={customerColumns}
          refreshKey={customerRows.map((c) => c.id).join('|')}
          actionsColumn={canWrite}
          galleryItems={customerRows.map((c) => ({
            id: c.id,
            icon: 'bi-person-badge',
            iconTone: 'primary' as const,
            title: c.code,
            subtitle: `${c.firstName} ${c.lastName}${c.companyName ? ` · ${c.companyName}` : ''}`,
            meta: `${c.email ?? '—'} · ${c.phone ?? '—'} · ${c.vehicleCount ?? 0} veh · ${c.activeSubscriptions ?? 0} subs`,
            actions: canWrite ? (
              <>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-primary"
                  aria-label="edit"
                  onClick={() => openEditCustomer(c)}
                >
                  <i className="bi bi-pencil" />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-icon btn-ghost-danger"
                  aria-label="delete"
                  onClick={() => {
                    if (window.confirm(t('customers.confirmDelete'))) {
                      deleteCustomer.mutate(c.id);
                    }
                  }}
                >
                  <i className="bi bi-trash" />
                </button>
              </>
            ) : undefined,
          }))}
        >
          {customerRows.map((c) => (
            <tr
              key={c.id}
              className={c.id === activeId ? 'table-active' : undefined}
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedId(c.id)}
            >
              <td className="fw-semibold">{c.code}</td>
              <td>
                {c.firstName} {c.lastName}
                {c.companyName ? ` · ${c.companyName}` : ''}
              </td>
              <td>{c.email ?? '—'}</td>
              <td>{c.phone ?? '—'}</td>
              <td>{c.vehicleCount ?? 0}</td>
              <td>{c.activeSubscriptions ?? 0}</td>
              {canWrite && (
                <td onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEditCustomer(c)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('customers.confirmDelete'))) {
                        deleteCustomer.mutate(c.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <Card
        title={t('customers.linkedVehicles')}
        className="mb-4"
        toolbar={
          canWrite ? (
            <button
              type="button"
              className="btn btn-light btn-sm"
              disabled={!activeId}
              onClick={() => {
                setFormError(null);
                setLinkVehicleId('');
                setLinkDialog(true);
              }}
            >
              {t('customers.linkVehicle')}
            </button>
          ) : undefined
        }
      >
        <ParkingDataTable
          storageKey="customers-vehicles"
          columns={vehicleColumns}
          refreshKey={vehicleRows.map((v) => v.id).join('|')}
          actionsColumn={canWrite}
          galleryItems={vehicleRows.map((v) => ({
            id: v.id,
            icon: 'bi-car-front',
            iconTone: 'info' as const,
            title: v.plate,
            subtitle: `${v.brand ?? '—'} ${v.model ?? ''}`.trim(),
            meta: enumLabel(t, 'vehicleType', v.type),
            actions: canWrite ? (
              <button
                type="button"
                className="btn btn-sm btn-icon btn-ghost-danger"
                onClick={() => unlinkVehicle.mutate(v.id)}
              >
                <i className="bi bi-trash" />
              </button>
            ) : undefined,
          }))}
        >
          {vehicleRows.map((v) => (
            <tr key={v.id}>
              <td className="fw-semibold">{v.plate}</td>
              <td>{v.brand ?? '—'}</td>
              <td>{v.model ?? '—'}</td>
              <td>{enumLabel(t, 'vehicleType', v.type)}</td>
              {canWrite && (
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    onClick={() => unlinkVehicle.mutate(v.id)}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      {canReadSub && (
        <Card
          title={t('customers.subscriptions')}
          className="mb-4"
          toolbar={
            canWriteSub ? (
              <button
                type="button"
                className="btn btn-light btn-sm"
                disabled={!activeId}
                onClick={openCreateSub}
              >
                {t('customers.createSub')}
              </button>
            ) : undefined
          }
        >
          <ParkingDataTable
            storageKey="customers-subscriptions"
            columns={subColumns}
            refreshKey={subRows.map((s) => s.id).join('|')}
            actionsColumn={canWriteSub}
            galleryItems={subRows.map((s) => ({
              id: s.id,
              icon: 'bi-calendar-check',
              iconTone: statusToneFromEnum(s.status),
              title: enumLabel(t, 'subscriptionPlan', s.plan),
              subtitle: `${(s.priceCents / 100).toFixed(2)} ${s.currency}`,
              meta: `${new Date(s.startsAt).toLocaleDateString()} → ${s.endsAt ? new Date(s.endsAt).toLocaleDateString() : '—'}`,
              badge: (
                <StatusBadge tone={statusToneFromEnum(s.status)}>
                  {enumLabel(t, 'subscriptionStatus', s.status)}
                </StatusBadge>
              ),
              actions: canWriteSub ? (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-primary"
                    aria-label="edit"
                    onClick={() => openEditSub(s)}
                  >
                    <i className="bi bi-pencil" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-icon btn-ghost-danger"
                    aria-label="delete"
                    onClick={() => {
                      if (window.confirm(t('customers.confirmDeleteSub'))) {
                        deleteSub.mutate(s.id);
                      }
                    }}
                  >
                    <i className="bi bi-trash" />
                  </button>
                </>
              ) : undefined,
            }))}
          >
            {subRows.map((s) => (
              <tr key={s.id}>
                <td>{enumLabel(t, 'subscriptionPlan', s.plan)}</td>
                <td>
                  <StatusBadge tone={statusToneFromEnum(s.status)}>
                    {enumLabel(t, 'subscriptionStatus', s.status)}
                  </StatusBadge>
                </td>
                <td>
                  {(s.priceCents / 100).toFixed(2)} {s.currency}
                </td>
                <td>{new Date(s.startsAt).toLocaleDateString()}</td>
                <td>{s.endsAt ? new Date(s.endsAt).toLocaleDateString() : '—'}</td>
                {canWriteSub && (
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-ghost-primary"
                      aria-label="edit"
                      onClick={() => openEditSub(s)}
                    >
                      <i className="bi bi-pencil" />
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-icon btn-ghost-danger"
                      aria-label="delete"
                      onClick={() => {
                        if (window.confirm(t('customers.confirmDeleteSub'))) {
                          deleteSub.mutate(s.id);
                        }
                      }}
                    >
                      <i className="bi bi-trash" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </ParkingDataTable>
        </Card>
      )}

      <AppModal
        open={customerDialog}
        onClose={closeCustomerDialog}
        title={editingCustomerId ? t('customers.edit') : t('customers.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeCustomerDialog}>
              {t('customers.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveCustomer.isPending}
              onClick={() => saveCustomer.mutate()}
            >
              {t('customers.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          {(
            [
              ['code', t('customers.code')],
              ['firstName', t('customers.firstName')],
              ['lastName', t('customers.lastName')],
              ['email', t('customers.email')],
              ['phone', t('customers.phone')],
              ['taxId', t('customers.taxId')],
              ['companyName', t('customers.company')],
            ] as const
          ).map(([key, label]) => (
            <div key={key}>
              <label className="form-label">{label}</label>
              <input
                className="form-control"
                value={form[key]}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                required={key === 'code' || key === 'firstName' || key === 'lastName'}
              />
            </div>
          ))}
        </div>
      </AppModal>

      <AppModal
        open={subDialog}
        onClose={closeSubDialog}
        title={editingSubId ? t('customers.editSub') : t('customers.createSub')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={closeSubDialog}>
              {t('customers.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={saveSub.isPending || (!editingSubId && !activeId)}
              onClick={() => saveSub.mutate()}
            >
              {t('customers.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          <div>
            <label className="form-label">{t('customers.plan')}</label>
            <select
              className="form-select"
              value={subForm.plan}
              onChange={(e) =>
                setSubForm((f) => ({
                  ...f,
                  plan: e.target.value as CreateSubscriptionInput['plan'],
                }))
              }
            >
              {['MONTHLY', 'QUARTERLY', 'YEARLY', 'COMPANY', 'EMPLOYEE', 'RESIDENT'].map((p) => (
                <option key={p} value={p}>
                  {enumLabel(t, 'subscriptionPlan', p)}
                </option>
              ))}
            </select>
          </div>
          {editingSubId && (
            <div>
              <label className="form-label">{t('customers.status')}</label>
              <select
                className="form-select"
                value={subForm.status}
                onChange={(e) =>
                  setSubForm((f) => ({
                    ...f,
                    status: e.target.value as NonNullable<CreateSubscriptionInput['status']>,
                  }))
                }
              >
                {['ACTIVE', 'PAST_DUE', 'CANCELLED', 'EXPIRED'].map((st) => (
                  <option key={st} value={st}>
                    {enumLabel(t, 'subscriptionStatus', st)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="form-label">{t('customers.priceCents')}</label>
            <input
              className="form-control"
              type="number"
              value={subForm.priceCents}
              onChange={(e) => setSubForm((f) => ({ ...f, priceCents: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('customers.startsAt')}</label>
            <input
              className="form-control"
              type="datetime-local"
              value={subForm.startsAt}
              onChange={(e) => setSubForm((f) => ({ ...f, startsAt: e.target.value }))}
            />
          </div>
          <div>
            <label className="form-label">{t('customers.endsAt')}</label>
            <input
              className="form-control"
              type="datetime-local"
              value={subForm.endsAt}
              onChange={(e) => setSubForm((f) => ({ ...f, endsAt: e.target.value }))}
            />
          </div>
        </div>
      </AppModal>

      <AppModal
        open={linkDialog}
        onClose={() => setLinkDialog(false)}
        title={t('customers.linkVehicle')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setLinkDialog(false)}>
              {t('customers.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!linkVehicleId || linkVehicle.isPending}
              onClick={() => linkVehicle.mutate()}
            >
              {t('customers.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          {formError && <div className="alert alert-danger mb-0">{formError}</div>}
          {!canWriteVehicle && (
            <div className="alert alert-info mb-0">{t('customers.linkHint')}</div>
          )}
          <div>
            <label className="form-label">{t('vehicles.plate')}</label>
            <select
              className="form-select"
              value={linkVehicleId}
              onChange={(e) => setLinkVehicleId(e.target.value)}
            >
              <option value="">—</option>
              {(allVehicles.data?.data ?? []).map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate} — {v.brand ?? ''} {v.model ?? ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </AppModal>
    </>
  );
}
